import {
  Home,
  HeartPulse,
  Stethoscope,
  Wallet,
  Users,
  Briefcase,
  Phone,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import {
  seniorPartnerSignals,
  seniorPillars,
  seniorLadder,
  seniorImpactStats,
} from "@/lib/proposalData";
import { useDeployment } from "@/lib/DeploymentContext";

const iconMap = {
  home: Home,
  "heart-pulse": HeartPulse,
  stethoscope: Stethoscope,
  wallet: Wallet,
  users: Users,
  briefcase: Briefcase,
};

const SENIOR_IMG =
  "https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function SeniorCarePipeline() {
  const d = useDeployment();
  return (
    <section
      id="seniors"
      data-testid="section-senior-care"
      className="bg-[#EBE6D8] py-24 lg:py-32 border-y border-[#CBD5E1]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                The Senior Care Pipeline · Aging in Place
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Same connector. Same multi-year horizon.<br />
              <em className="italic">Different ending: dignity at home.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3 text-[#334155] leading-relaxed">
            <p>
              The senior track is not a separate program — it is the same Bridge
              connector working a second cohort under the same roof. The destination
              changes: instead of a mortgage, the goal is aging safely at home with
              dignity, daily support, and a phone that still rings on Sunday.
            </p>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-[#64748B]">
              Sources — Houston-Harris County AAA (h-gac.com/area-agency-on-aging),
              Harris County RIDES (rides.harriscountytx.gov), Interfaith Ministries
              Meals on Wheels, YWCA Houston, Medicare.gov.
            </p>
          </div>
        </div>

        {/* Partner signal cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1] mb-16">
          {seniorPartnerSignals.map((p, i) => (
            <div
              key={p.partner}
              data-testid={`senior-partner-${i}`}
              className="bg-white p-6 lg:p-7 flex flex-col"
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-3">
                {p.partner}
              </div>
              <div className="font-display text-5xl lg:text-6xl text-[#0A192F] leading-none">
                {p.headline}
              </div>
              <p className="mt-4 text-sm text-[#334155] leading-relaxed flex-1">{p.body}</p>
              <div className="mt-4 pt-3 border-t border-[#CBD5E1] font-mono text-[9px] uppercase tracking-widest text-[#64748B]">
                {p.source}
              </div>
            </div>
          ))}
        </div>

        {/* Editorial image + mission */}
        <div className="grid lg:grid-cols-12 gap-8 mb-16">
          <div className="lg:col-span-5">
            <div className="relative w-full aspect-[4/5] overflow-hidden border border-[#CBD5E1]">
              <img
                src={SENIOR_IMG}
                alt="Senior aging in place with daily support"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-[#8B0000] text-[#F9F7F1] px-5 py-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
                  Aging-in-Place
                </div>
                <div className="font-display text-xl leading-tight">
                  Safe at home. Connected. With dignity.
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 bg-[#0A192F] text-[#F9F7F1] p-8 lg:p-10 relative overflow-hidden flex flex-col justify-center">
            <div className="absolute inset-0 grain-overlay opacity-25 pointer-events-none" />
            <div className="relative z-10">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-3">
                Why the Senior Track exists
              </div>
              <h3 className="font-display text-2xl lg:text-4xl leading-tight">
                A senior who falls at home loses the home. A senior who is isolated loses years.
              </h3>
              <p className="mt-5 text-[#CBD5E1] leading-relaxed">
                {d.seniorPipelineMultigen}
                mother working through {d.college} often has a parent under her roof
                whose missed Medicare appointment, expired SNAP, or unsafe shower will
                become next year&apos;s nursing-home placement. The Bridge connector serves
                both — same intake day, same household, one relationship.
              </p>

              <div className="mt-7 grid sm:grid-cols-2 gap-px bg-[#F9F7F1]/15 border border-[#F9F7F1]/15">
                {seniorImpactStats.map((s, i) => (
                  <div key={i} className="bg-[#0A192F] p-4">
                    <div className="font-display text-2xl text-[#D4AF37]">{s.k}</div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-[#CBD5E1] mt-1 leading-snug">
                      {s.v}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Six pillars */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-2xl lg:text-3xl text-[#0A192F]">
              Six pillars. <em className="italic">One household.</em>
            </h3>
            <span className="hidden md:inline font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">
              Senior Service Map
            </span>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
            {seniorPillars.map((p, i) => {
              const Icon = iconMap[p.icon] || Home;
              return (
                <article
                  key={p.name}
                  data-testid={`senior-pillar-${i}`}
                  className="bg-white p-6 lg:p-7 flex flex-col group transition-colors hover:bg-[#F9F7F1]"
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className="grid place-items-center w-12 h-12 border border-[#8B0000] text-[#8B0000] group-hover:bg-[#8B0000] group-hover:text-[#F9F7F1] transition-colors">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mt-2">
                      0{i + 1}
                    </span>
                  </div>
                  <h4 className="font-display text-xl text-[#0A192F]">{p.name}</h4>
                  <div className="mt-1 font-display italic text-sm text-[#8B0000]">
                    {p.headline}
                  </div>
                  <p className="mt-4 text-sm text-[#475569] leading-relaxed flex-1">{p.body}</p>
                </article>
              );
            })}
          </div>
        </div>

        {/* Senior stability ladder */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-8 bg-[#8B0000]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
              The Aging-in-Place Ladder
            </span>
          </div>
          <h3 className="font-display text-2xl lg:text-4xl text-[#0A192F] leading-tight mb-2">
            From crisis to <em className="italic text-[#8B0000]">aging in place</em>.
          </h3>
          <p className="text-[#334155] max-w-3xl mb-10">
            The senior cohort is tracked on a parallel 12-month ladder. Each rung is
            a measurable, billable connector outcome — what Bridge reports back to
            HUD, {d.seniorPipelineHandoffs}
          </p>

          <ol className="grid lg:grid-cols-5 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
            {seniorLadder.map((rung, i) => {
              const isLast = rung.highlight;
              return (
                <li
                  key={rung.step}
                  data-testid={`senior-rung-${i}`}
                  className={`p-6 lg:p-7 flex flex-col ${
                    isLast ? "bg-[#0A192F] text-[#F9F7F1]" : "bg-white"
                  }`}
                >
                  <div
                    className={`font-mono text-[10px] uppercase tracking-widest mb-3 ${
                      isLast ? "text-[#D4AF37]" : "text-[#8B0000]"
                    }`}
                  >
                    {rung.step}
                  </div>
                  <div
                    className={`font-display text-2xl lg:text-3xl leading-tight ${
                      isLast ? "text-[#F9F7F1]" : "text-[#0A192F]"
                    }`}
                  >
                    {rung.state}
                  </div>
                  <div
                    className={`mt-1 font-mono text-[10px] uppercase tracking-widest ${
                      isLast ? "text-[#D4AF37]" : "text-[#64748B]"
                    }`}
                  >
                    {rung.label}
                  </div>
                  <p
                    className={`mt-4 text-xs leading-relaxed flex-1 ${
                      isLast ? "text-[#CBD5E1]" : "text-[#475569]"
                    }`}
                  >
                    {rung.detail}
                  </p>
                  {isLast && (
                    <ShieldCheck
                      data-testid="aging-in-place-icon"
                      className="h-6 w-6 text-[#D4AF37] mt-5"
                    />
                  )}
                  {!isLast && i < seniorLadder.length - 1 && (
                    <ArrowRight className="h-4 w-4 text-[#8B0000] mt-5" />
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        {/* Closing wrap */}
        <div className="bg-white border border-[#CBD5E1] p-6 lg:p-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
          <div className="flex items-start gap-4">
            <span className="grid place-items-center w-12 h-12 bg-[#8B0000] text-[#F9F7F1] shrink-0">
              <Phone className="h-5 w-5" />
            </span>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-1">
                One number to call
              </div>
              <p className="text-[#0A192F] leading-relaxed max-w-2xl">
                Every Bridge senior gets a single phone number — their connector — for
                every benefit, ride, prescription, and home repair. No more 2-1-1
                roulette. No more hold music. <em className="italic">One human relationship.</em>
              </p>
            </div>
          </div>
          <a
            href="#contact"
            data-testid="senior-cta"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#8B0000] hover:text-[#0A192F] whitespace-nowrap"
          >
            Fund the senior track <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
}
